import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { fetchWithAuth } from '../lib/api';

interface SessionWithSummary {
  sessionId: string;
  title: string;
  bullets: string[];
  areaTags?: string[];
  photoUrl?: string;
}

interface CurriculumArea {
  id: string;
  title: string;
  enabled: boolean;
  order: number;
}

interface ChapterAssignmentProps {
  restUrl: string;
  onApprove: (assignment: Record<string, string[]>) => void;
  onCancel: () => void;
}

function GripIcon() {
  return (
    <svg
      className="w-4 h-4 text-stone-300 flex-shrink-0"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="5" cy="4" r="1.5" />
      <circle cx="11" cy="4" r="1.5" />
      <circle cx="5" cy="8" r="1.5" />
      <circle cx="11" cy="8" r="1.5" />
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="11" cy="12" r="1.5" />
    </svg>
  );
}


function CheckIcon() {
  return (
    <svg
      className="w-4 h-4 text-green-600 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      className="animate-spin h-5 w-5 text-amber-500 flex-shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

interface SessionCardProps {
  session: SessionWithSummary;
  bucketId: string;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, sessionId: string, sourceBucketId: string) => void;
  onDragEnd: () => void;
}

function SessionCard({ session, bucketId, isDragging, onDragStart, onDragEnd }: SessionCardProps) {
  const cleanTitle = DOMPurify.sanitize(session.title, { ALLOWED_TAGS: [] });
  const cleanBullets = session.bullets.map(b => DOMPurify.sanitize(b, { ALLOWED_TAGS: [] }));

  return (
    <div
      role="listitem"
      draggable
      onDragStart={(e) => onDragStart(e, session.sessionId, bucketId)}
      onDragEnd={onDragEnd}
      className={`bg-white border rounded-lg p-3 transition select-none ${
        isDragging
          ? 'border-amber-400 shadow-md opacity-90 cursor-grabbing'
          : 'border-stone-200 cursor-grab hover:shadow-sm'
      }`}
    >
      <div className="flex items-start gap-2">
        <div aria-label="Drag to reorder">
          <GripIcon />
        </div>
        <p className="text-sm font-semibold text-stone-800 line-clamp-2 flex-1">{cleanTitle || 'Untitled session'}</p>
        {session.photoUrl && (
          <img
            src={session.photoUrl}
            alt=""
            aria-hidden="true"
            className="w-10 h-10 rounded object-cover flex-shrink-0"
          />
        )}
      </div>
      {cleanBullets.length > 0 ? (
        <ul className="mt-1 space-y-1 ml-6">
          {cleanBullets.slice(0, 4).map((bullet, i) => (
            <li key={i} className="flex items-start gap-1">
              <span className="w-1 h-1 rounded-full bg-stone-300 mt-2 flex-shrink-0" aria-hidden="true" />
              <span className="text-xs text-stone-500 leading-relaxed">{bullet}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 ml-6 text-xs text-stone-400 italic">Summary not yet available</p>
      )}
    </div>
  );
}

interface BucketDropZoneProps {
  bucketId: string;
  sessions: SessionWithSummary[];
  draggedSessionId: string | null;
  dragOverBucketId: string | null;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnter: (bucketId: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, bucketId: string) => void;
  onDragStart: (e: React.DragEvent, sessionId: string, sourceBucketId: string) => void;
  onDragEnd: () => void;
  emptyLabel?: string;
  isOver: boolean;
}

function BucketDropZone({
  bucketId,
  sessions,
  draggedSessionId,
  dragOverBucketId: _dragOverBucketId,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  emptyLabel = 'Drop sessions here',
  isOver,
}: BucketDropZoneProps) {
  if (sessions.length === 0) {
    return (
      <div
        onDragOver={onDragOver}
        onDragEnter={() => onDragEnter(bucketId)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, bucketId)}
        className={`border-2 border-dashed rounded-lg h-12 flex items-center justify-center transition ${
          isOver
            ? 'bg-stone-100 border-stone-300'
            : 'border-stone-200'
        }`}
      >
        <span className="text-xs text-stone-400">{emptyLabel}</span>
      </div>
    );
  }

  return (
    <div
      role="list"
      onDragOver={onDragOver}
      onDragEnter={() => onDragEnter(bucketId)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, bucketId)}
      className={`space-y-2 rounded-lg min-h-12 transition ${
        isOver ? 'bg-stone-100' : ''
      }`}
    >
      {sessions.map((session) => (
        <SessionCard
          key={session.sessionId}
          session={session}
          bucketId={bucketId}
          isDragging={draggedSessionId === session.sessionId}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        />
      ))}
    </div>
  );
}

function SkeletonBucket() {
  return (
    <div className="space-y-2">
      {[80, 80, 80].map((h, i) => (
        <div
          key={i}
          className="bg-stone-100 animate-pulse rounded-lg"
          style={{ height: `${h}px` }}
        />
      ))}
    </div>
  );
}

export function ChapterAssignment({ restUrl, onApprove, onCancel }: ChapterAssignmentProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionWithSummary[]>([]);
  const [areas, setAreas] = useState<CurriculumArea[]>([]);
  const [buckets, setBuckets] = useState<Record<string, string[]>>({});
  const [draggedSessionId, setDraggedSessionId] = useState<string | null>(null);
  const [dragOverBucketId, setDragOverBucketId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);

    const base = restUrl.replace(/\/$/, '');

    Promise.all([
      fetchWithAuth(`${base}/sessions/summaries`)
        .then(r => r.ok ? r.json() as Promise<{ summaries: Array<{ sessionId: string; title: string; bullets: string[]; createdAt: string }> }> : Promise.reject(new Error('summaries fetch failed')))
        .then(d => d.summaries),
      fetchWithAuth(`${base}/sessions`)
        .then(r => r.ok ? r.json() as Promise<{ sessions: Array<{ sessionId: string; title?: string; areaTags?: string[] }> }> : Promise.reject(new Error('sessions fetch failed')))
        .then(d => d.sessions),
      fetchWithAuth(`${base}/curriculum`)
        .then(r => r.ok ? r.json() as Promise<{ areas: CurriculumArea[] }> : Promise.reject(new Error('curriculum fetch failed')))
        .then(d => d.areas),
      fetchWithAuth(`${base}/photos`)
        .then(r => r.ok ? r.json() as Promise<{ photos: Array<{ status: string; viewUrl: string | null; linkedMemoryIds: string[] }> }> : Promise.resolve({ photos: [] }))
        .then(d => d.photos),
    ])
      .then(([summaries, rawSessions, fetchedAreas, photos]) => {
        const summaryMap = new Map(summaries.map(s => [s.sessionId, s]));

        // linkedMemoryIds on photos stores sessionIds (written by linkPhotoToSession)
        const sessionPhotoMap = new Map<string, string>();
        for (const photo of photos) {
          if (photo.status === 'ready' && photo.viewUrl) {
            for (const sid of (photo.linkedMemoryIds ?? [])) {
              if (!sessionPhotoMap.has(sid)) {
                sessionPhotoMap.set(sid, photo.viewUrl);
              }
            }
          }
        }

        const merged: SessionWithSummary[] = rawSessions.map(s => {
          const summary = summaryMap.get(s.sessionId);
          return {
            sessionId: s.sessionId,
            title: summary?.title ?? s.title ?? '',
            bullets: summary?.bullets ?? [],
            areaTags: s.areaTags,
            photoUrl: sessionPhotoMap.get(s.sessionId),
          };
        });

        const sortedAreas = [...fetchedAreas].sort((a, b) => a.order - b.order);

        const init: Record<string, string[]> = { '__unassigned': [] };
        for (const area of sortedAreas) {
          init[area.id] = [];
        }

        for (const session of merged) {
          const primaryArea = session.areaTags?.[0] ?? '__unassigned';
          const targetBucket = primaryArea in init ? primaryArea : '__unassigned';
          init[targetBucket].push(session.sessionId);
        }

        setSessions(merged);
        setAreas(sortedAreas);
        setBuckets(init);
      })
      .catch(() => setError('Could not load session summaries. Retry.'))
      .finally(() => setLoading(false));
  }, [restUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDragStart = (e: React.DragEvent, sessionId: string, sourceBucketId: string) => {
    e.dataTransfer.setData('sessionId', sessionId);
    e.dataTransfer.setData('sourceBucketId', sourceBucketId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedSessionId(sessionId);
  };

  const handleDragEnd = () => {
    setDraggedSessionId(null);
    setDragOverBucketId(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (bucketId: string) => {
    setDragOverBucketId(bucketId);
  };

  const handleDragLeave = () => {
    setDragOverBucketId(null);
  };

  const handleDrop = (e: React.DragEvent, targetBucketId: string) => {
    e.preventDefault();
    const sessionId = e.dataTransfer.getData('sessionId');
    if (!sessionId) return;

    setBuckets(prev => {
      const next: Record<string, string[]> = {};
      for (const [bid, ids] of Object.entries(prev)) {
        next[bid] = ids.filter(id => id !== sessionId);
      }
      if (targetBucketId in next) {
        next[targetBucketId] = [...next[targetBucketId], sessionId];
      }
      return next;
    });

    // Persist the assignment so it sticks on next load
    const newTags = targetBucketId === '__unassigned' ? [] : [targetBucketId];
    const base = restUrl.replace(/\/$/, '');
    fetchWithAuth(`${base}/sessions/${sessionId}/tags`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ areaTags: newTags }),
    }).catch(() => {});

    setDraggedSessionId(null);
    setDragOverBucketId(null);
  };

  const sessionMap = new Map(sessions.map(s => [s.sessionId, s]));

  const getBucketSessions = (bucketId: string): SessionWithSummary[] =>
    (buckets[bucketId] ?? []).map(id => sessionMap.get(id)).filter(Boolean) as SessionWithSummary[];

  const unassignedSessions = getBucketSessions('__unassigned');
  const unassignedCount = unassignedSessions.length;

  const enabledAreas = areas.filter(a => a.enabled);

  const handleApprove = () => {
    onApprove(buckets);
  };

  const ctaButton = (
    <button
      type="button"
      onClick={handleApprove}
      disabled={loading}
      aria-disabled={loading ? 'true' : undefined}
      className={`bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${
        loading ? 'bg-amber-300 cursor-not-allowed opacity-60' : ''
      }`}
    >
      Generate with this arrangement
    </button>
  );

  return (
    <div
      role="main"
      aria-label="Chapter assignment"
      className="flex-1 flex flex-col overflow-hidden bg-stone-50"
    >
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-stone-200 px-6 h-14 flex items-center gap-3 z-10">
        <span className="text-stone-800 font-semibold text-sm">Arrange Your Chapters</span>
        {loading && (
          <div className="flex items-center gap-1.5 ml-2">
            <SpinnerIcon />
            <span className="text-xs text-stone-400">Loading...</span>
          </div>
        )}
        <div className="flex-1" />
        {error ? (
          <button
            type="button"
            onClick={load}
            className="text-sm text-amber-600 hover:text-amber-700 font-semibold"
          >
            Retry
          </button>
        ) : (
          ctaButton
        )}
        <button
          type="button"
          onClick={onCancel}
          className="text-stone-500 hover:text-stone-700 text-sm transition-colors"
        >
          Back
        </button>
      </div>

      {/* Two-column body */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row">

        {/* Left pane — sessions source pool */}
        <div className="md:w-2/5 flex flex-col border-b md:border-b-0 md:border-r border-stone-200 overflow-hidden">
          <div className="flex-shrink-0 px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <span className="text-sm font-semibold text-amber-800">Sessions</span>
            {unassignedCount > 0 ? (
              <>
                <span
                  aria-label={`${unassignedCount} unassigned`}
                  className="bg-amber-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full"
                >
                  {unassignedCount}
                </span>
                <span className="text-xs text-amber-600 ml-auto">drag to a chapter →</span>
              </>
            ) : (
              <div className="flex items-center gap-1 ml-1">
                <CheckIcon />
                <span className="text-xs text-stone-500">All assigned</span>
              </div>
            )}
          </div>

          <div
            role="region"
            aria-label="Unassigned sessions"
            onDragOver={handleDragOver}
            onDragEnter={() => handleDragEnter('__unassigned')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, '__unassigned')}
            className={`flex-1 overflow-y-auto p-3 space-y-2 transition-colors ${
              dragOverBucketId === '__unassigned' ? 'bg-amber-50' : ''
            }`}
          >
            {loading ? (
              <SkeletonBucket />
            ) : unassignedSessions.length === 0 ? (
              <div className={`rounded-xl border-2 border-dashed h-20 flex items-center justify-center transition-colors ${
                dragOverBucketId === '__unassigned'
                  ? 'border-amber-400 bg-amber-50'
                  : 'border-amber-200'
              }`}>
                <span className="text-xs text-amber-500">← Drop sessions back here</span>
              </div>
            ) : (
              <div role="list" className="space-y-2">
                {unassignedSessions.map(session => (
                  <SessionCard
                    key={session.sessionId}
                    session={session}
                    bucketId="__unassigned"
                    isDragging={draggedSessionId === session.sessionId}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right pane — chapter buckets */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 px-4 py-2.5 bg-white border-b border-stone-100 flex items-center gap-2">
            <span className="text-sm font-semibold text-stone-700">Chapters</span>
            <span className="text-xs text-stone-400">{enabledAreas.length} chapters</span>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loading ? (
              <SkeletonBucket />
            ) : error ? (
              <p className="text-sm text-red-600 p-2">{error}</p>
            ) : (
              enabledAreas.map(area => {
                const areaSessions = getBucketSessions(area.id);
                const isOver = dragOverBucketId === area.id;

                return (
                  <div
                    key={area.id}
                    role="region"
                    aria-label={`${area.title} — ${areaSessions.length} sessions`}
                    className={`border rounded-xl p-3 transition-colors ${
                      isOver
                        ? 'border-amber-300 bg-amber-50'
                        : 'border-stone-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-sm font-semibold text-stone-700">{area.title}</span>
                      <span className="text-xs text-stone-400">
                        {areaSessions.length} session{areaSessions.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <BucketDropZone
                      bucketId={area.id}
                      sessions={areaSessions}
                      draggedSessionId={draggedSessionId}
                      dragOverBucketId={dragOverBucketId}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      isOver={isOver}
                    />
                  </div>
                );
              })
            )}

            <p className="text-xs text-stone-400 text-center pt-2 pb-4">
              Unassigned sessions will not appear in your book.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
